import { List, Form, Input, Spin, Popconfirm, Row, Card, Col, Image, Modal, Tooltip } from "antd";
import { Button } from "Components/General/Button";
import { withModal, WithModal } from "Components/General/Modal/withModal";
import { I18N_NS } from "Config/i18n";
import { dayjs } from "Config/dayjs";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { observer } from "mobx-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { projectsDataStore } from "Stores";
import { contactsStore } from "Stores/ContactsStore";
import { BcfIssueEditComment } from "./BcfIssueEditComment";
import { placeholderPath } from "utils/placeholderPath";
import { ViewpointManager, ViewpointWithImage } from "./ViewpointManager";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { xktViewerStore } from "Components/XKTViewer/Stores/XKTViewerStore";
import { notificationSuccess } from "utils/libHelpers/AntdNotification";
import { resolveViewpointModel, hasAnyViewableModel } from "Components/XKTViewer/utils/resolveViewpointModel";

interface BcfIssueCommentsProps extends WithModal {
  topicId: string;
  topicActions: { [action in API_BCFSERVERPROJECTEXTENSIONS_TOPIC_ACTIONS]?: boolean };
  editMode?: boolean;
  conflictComments?: API_BcfComment[];
  topicDeleted?: boolean;
  isInImportMode?: boolean;
  viewpointManager?: ViewpointManager;
  viewpoints?: API_BcfServerViewpoint[];
  showDeletedTopics?: boolean;
  showViewInBimViewer?: boolean;
  issueModels?: API_BcfServerIfcFileInfo[];
  fetchedComments?: API_BcfServerComment[];
  onCommentsUpdate?: (topicId: string) => void;
}

interface CommentListProps {
  comments: API_BcfServerComment[];
  loading: boolean;
  onDelete: (comment: API_BcfServerComment) => Promise<void>;
  onEdit: (comment: API_BcfServerComment) => void;
  getViewpointIMG: (
    viewpoint_guid: string,
    isInImportMode?: boolean
  ) => (API_BcfServerViewpoint & { imageSrc?: string }) | ViewpointWithImage;
  showViewInBimViewer?: boolean;
  issueModels?: API_BcfServerIfcFileInfo[];
  viewViewpoint: (viewpoint: API_BcfServerViewpoint, issueModels: API_BcfServerIfcFileInfo[]) => Promise<void>;
  getViewpoint: (guid: string) => ViewpointWithImage;
}
//        renderItem={(props: API_BcfServerComment & API_BcfConflictComment) => (

const CommentList = React.memo(
  ({
    comments,
    loading,
    onDelete,
    onEdit,
    getViewpointIMG,
    showViewInBimViewer,
    issueModels,
    viewViewpoint,
    getViewpoint,
  }: CommentListProps) => {
    const { t } = useTranslation(I18N_NS.PROJECT);

    const handleEdit = React.useCallback((comment: API_BcfServerComment) => () => onEdit(comment), [onEdit]);
    const handleDelete = React.useCallback((comment: API_BcfServerComment) => () => onDelete(comment), [onDelete]);

    return (
      <Spin spinning={loading}>
        <List
          dataSource={comments}
          // header={`${comments.length} ${comments.length > 1 ? "comments" : "comment"}`}
          itemLayout="vertical"
          id="bcf_comments_list"
          renderItem={(props: API_BcfServerComment & API_BcfConflictComment) => {
            const viewpointId = props.viewpoint_guid || props.viewpointGuid;
            const viewpoint = viewpointId ? getViewpointIMG(viewpointId) : undefined;

            return (
              <List.Item
                key={props.id}
                actions={[
                  props.authorization?.comment_actions.includes("update") && (
                    <Button key="1" type="text" size="small" faIcon={"edit"} onClick={() => handleEdit(props)}></Button>
                  ),
                  props.authorization?.comment_actions.includes("delete") && (
                    <Popconfirm
                      title={t("Are you sure?")}
                      onConfirm={() => handleDelete(props)}
                      getPopupContainer={() => document.getElementById("bcf_comments_list")}
                    >
                      <Button key="2" type="text" size="small" faIcon={"trash-alt"}></Button>
                    </Popconfirm>
                  ),
                ]}
              >
                <List.Item.Meta
                  title={contactsStore.getUserNameById(props.authorId, false) || props.author}
                  description={dayjs(props.date).format("lll")}
                />
                {viewpoint && (
                  <Col sm={8} md={6} className="mb-2 relative">
                    <Card>
                      <div className="viewpoint-actions">
                        {showViewInBimViewer && issueModels?.length !== 0 && (
                          <Tooltip
                            title={
                              hasAnyViewableModel(issueModels)
                                ? t("View with BIM Viewer")
                                : t("BIM Viewer (being processed)")
                            }
                            placement="bottom"
                          >
                            <div
                              className="mr-2"
                              onClick={() =>
                                hasAnyViewableModel(issueModels) &&
                                viewViewpoint(getViewpoint(viewpointId), issueModels)
                              }
                            >
                              <FontAwesomeIcon fixedWidth icon={["far", "bullseye-pointer"]} />
                            </div>
                          </Tooltip>
                        )}
                      </div>
                      <Image
                        src={
                          viewpoint.imageSrc ||
                          (viewpoint.snapshot
                            ? `data:image/${viewpoint.snapshot.snapshot_type};base64,${viewpoint.snapshot.snapshot_data}`
                            : placeholderPath)
                        }
                        fallback={placeholderPath}
                        preview
                      />
                    </Card>
                  </Col>
                )}
                {props.comment}
              </List.Item>
            );
          }}
        />
      </Spin>
    );
  }
);

interface EditorProps {
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  value: string;
  disabled?: boolean;
}

const Editor = ({ onChange, value, disabled }: EditorProps) => {
  const { t } = useTranslation([I18N_NS.PROJECT, I18N_NS.XKT_VIEWER]);

  return (
    <>
      <Form.Item>
        <Input.TextArea placeholder={t("Comment")} rows={4} onChange={onChange} value={value} disabled={disabled} />
      </Form.Item>
    </>
  );
};

interface ViewpointsListProps {
  viewpointManager?: ViewpointManager;
  viewpoints?: API_BcfViewpoint[] | API_BcfServerViewpoint[];
  isModalOpen?: boolean;
  setIsModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedViewpoint?: React.Dispatch<React.SetStateAction<ViewpointWithImage>>;
  selectedViewpoint?: string;
  isInImportMode?: boolean;
}

const ViewpointsListModal = ({
  viewpointManager,
  viewpoints,
  isModalOpen,
  setIsModalOpen,
  setSelectedViewpoint,
  isInImportMode,
}: ViewpointsListProps) => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  const [selected, setSelected] = React.useState<ViewpointWithImage>();

  const list = React.useMemo(
    () => (isInImportMode ? viewpoints : viewpointManager?.viewpoints || []),
    [isInImportMode, viewpoints, viewpointManager?.viewpoints]
  );

  return (
    <Modal
      title={t("Select Viewpoint")}
      open={isModalOpen}
      onOk={() => {
        setSelectedViewpoint(selected);
        setIsModalOpen(false);
      }}
      onCancel={() => setIsModalOpen(false)}
      okText={t("Add")}
      width="100%"
      style={{ maxWidth: 1000 }}
      zIndex={2020}
      destroyOnClose
    >
      <Row gutter={10}>
        {list.map((item, index) => (
          <Col sm={8} md={6} key={item?.guid ?? index} className="mb-2 relative">
            <Card
              onClick={() => setSelected(item as ViewpointWithImage)}
              style={{
                border: selected?.guid === item?.guid ? "3px solid #1890ff" : "1px solid #f0f0f0",
              }}
            >
              <Image
                src={
                  (item as ViewpointWithImage)?.imageSrc ||
                  (item?.snapshot
                    ? `data:image/${item.snapshot.snapshot_type};base64,${item.snapshot.snapshot_data}`
                    : placeholderPath)
                }
                fallback={placeholderPath}
                preview={false}
              />
            </Card>
          </Col>
        ))}
      </Row>
    </Modal>
  );
};

export const BcfIssueComments = withModal(
  observer((props: BcfIssueCommentsProps) => {
    const { t } = useTranslation(I18N_NS.PROJECT);
    const [comments, setComments] = React.useState<API_BcfServerComment[]>(props.fetchedComments || []);
    const [loading, setLoading] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [value, setValue] = React.useState("");
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [selectedViewpoint, setSelectedViewpoint] = React.useState<ViewpointWithImage>();
    const [isSubmittingViewpoint, setIsSubmittingViewpoint] = React.useState(false);

    // Update local comments when fetchedComments from parent changes
    React.useEffect(() => {
      if (props.fetchedComments) {
        setComments(props.fetchedComments);
      }
    }, [props.fetchedComments]);

    const fetchComments = async (topicId: string) => {
      // If parent provides onCommentsUpdate, use that instead
      if (props.onCommentsUpdate) {
        props.onCommentsUpdate(topicId);
        return;
      }

      // Otherwise, fetch comments locally
      try {
        setLoading(true);
        if (props.isInImportMode) {
          setComments(props.conflictComments);
        } else {
          const response = await API.getData<API_BcfServerComment[]>(
            API_URL.BCF_SERVER_COMMENTS.bcfServerGetComments(projectsDataStore.bcfGuid, topicId),
            { $orderby: "date asc", includeAuthorization: true }
          );
          setComments(response);
        }
      } catch (error) {
        throw error;
      } finally {
        setLoading(false);
      }
    };

    const handleDelete = async (comment: API_BcfServerComment) => {
      try {
        await API.deleteData(
          API_URL.BCF_SERVER_COMMENTS.bcfServerDeleteComment(
            projectsDataStore.bcfGuid,
            comment.topic_guid,
            comment.guid
          )
        );
        setComments(comments.filter((item) => item.guid !== comment.guid));
      } catch (error) {
        throw error;
      }
    };

    const handleEditComment = async (record: API_BcfServerComment) => {
      await API.editData(
        API_URL.BCF_SERVER_COMMENTS.bcfServerPutComment(projectsDataStore.bcfGuid, record.topic_guid, record.guid),
        { comment: record.comment }
      );
      // notificationSuccess(t("Success"), t("Comment was edited"));
      fetchComments(record.topic_guid);
    };

    const handleEdit = (comment: API_BcfServerComment) => {
      props.showModal(BcfIssueEditComment, {
        modalProps: {
          footer: null,
          width: 500,
          zIndex: 100012,
          title: t("Edit comment"),
        },
        componentProps: {
          onSubmit: handleEditComment,
          record: comment,
        },
      });
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setValue(e.target.value);
    };

    const handleSubmit = async () => {
      try {
        setSubmitting(true);

        let viewpointGuid = selectedViewpoint?.guid;

        // If the selected viewpoint needs to be submitted, submit it first
        if (selectedViewpoint?.toSubmit) {
          viewpointGuid = await submitViewpoint();
        }

        await API.newData<API_BcfServerComment>(
          API_URL.BCF_SERVER_COMMENTS.bcfServerPostComments(projectsDataStore.bcfGuid, props.topicId),
          { comment: value, viewpoint_guid: viewpointGuid }
        );
        fetchComments(props.topicId);
        setValue("");
        setSelectedViewpoint(null);
      } catch (error) {
        throw error;
      } finally {
        setSubmitting(false);
      }
    };

    const reFetch = React.useCallback(() => fetchComments(props.topicId), [props.topicId]);

    React.useEffect(() => {
      // Only fetch if comments are not already provided by parent
      if (!props.fetchedComments) {
        fetchComments(props.topicId);
      }
    }, [props.topicId]);

    // Track if selectedViewpoint still exists in viewpointManager (e.g., not deleted in another tab)
    React.useEffect(() => {
      if (selectedViewpoint && props.viewpointManager?.viewpoints) {
        const viewpointExists = props.viewpointManager.viewpoints.some((vp) => vp.guid === selectedViewpoint.guid);
        if (!viewpointExists) {
          setSelectedViewpoint(null);
        }
      }
    }, [props.viewpointManager?.viewpoints, selectedViewpoint]);

    const removeViewpoint = () => {
      setSelectedViewpoint(null);
    };

    const submitViewpoint = async (): Promise<string | null> => {
      if (selectedViewpoint && props.viewpointManager) {
        try {
          setIsSubmittingViewpoint(true);

          // Submit only the selected viewpoint
          const newGuid = await props.viewpointManager.saveSingleViewpoint(selectedViewpoint.guid);

          if (newGuid) {
            // Find the updated viewpoint with the new GUID
            const updatedViewpoint = props.viewpointManager.viewpoints.find((vp) => vp.guid === newGuid);
            if (updatedViewpoint) {
              setSelectedViewpoint({ ...updatedViewpoint });
              notificationSuccess(t("Success"), t("Viewpoint submitted successfully"));
            } else {
              // If viewpoint not found, clear selection
              setSelectedViewpoint(null);
            }
          } else {
            // If no new GUID returned, clear selection
            setSelectedViewpoint(null);
          }

          setIsSubmittingViewpoint(false);
          return newGuid;
        } catch (error) {
          setIsSubmittingViewpoint(false);
          throw error;
        }
      }
      // Return null if no viewpoint or viewpointManager - fallback for edge cases
      return null;
    };

    const getViewpointIMG = (viewpoint_guid: string): API_BcfServerViewpoint | ViewpointWithImage | undefined => {
      return props.isInImportMode
        ? props?.viewpoints.find((item) => item.guid === viewpoint_guid)
        : props.viewpointManager?.viewpoints.find((item) => item.guid === viewpoint_guid);
    };

    const viewViewpoint = async (viewpoint: API_BcfServerViewpoint, issueModels: API_BcfServerIfcFileInfo[]) => {
      const targetModel = resolveViewpointModel(issueModels);
      if (!targetModel) return;

      await xktViewerStore.initViewer(
        {
          fileName: targetModel.fileName,
          fileId: targetModel.id,
          initialViewpoint: {
            viewpoint: viewpoint,
            topicId: props.viewpointManager.topicId,
            fileId: targetModel.id,
          },
        },
        issueModels,
        props.topicId,
        props.showDeletedTopics
      );
    };

    const getViewpoint = (guid: string) => {
      return props.viewpointManager.viewpoints.find((item) => item.guid === guid);
    };

    const hasViewpoints = React.useMemo(() => {
      const viewpointsList = props.isInImportMode ? props.viewpoints : props.viewpointManager?.viewpoints;
      return viewpointsList && viewpointsList.length > 0;
    }, [props.isInImportMode, props.viewpoints, props.viewpointManager?.viewpoints]);

    return (
      <>
        <ViewpointsListModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          viewpointManager={props.viewpointManager}
          viewpoints={props.viewpoints}
          setSelectedViewpoint={setSelectedViewpoint}
          isInImportMode={props.isInImportMode}
        />
        <Row style={{ marginBottom: 10 }}>
          <Button
            style={{ marginRight: 10 }}
            onClick={reFetch}
            loading={loading}
            faIcon={"sync"}
            disabled={props.isInImportMode}
          >
            {t("Refresh")}
          </Button>
        </Row>
        {comments?.length > 0 && (
          <CommentList
            comments={comments}
            loading={loading}
            onDelete={handleDelete}
            onEdit={handleEdit}
            getViewpointIMG={getViewpointIMG}
            showViewInBimViewer={props.showViewInBimViewer}
            issueModels={props?.issueModels}
            viewViewpoint={viewViewpoint}
            getViewpoint={getViewpoint}
          />
        )}
        {props.topicActions.createComment && (
          <Editor onChange={handleChange} value={value} disabled={props.isInImportMode || props.topicDeleted} />
        )}
        {selectedViewpoint && (
          <Col sm={8} md={6} className="mb-2 relative">
            <Spin spinning={isSubmittingViewpoint} tip={t("Submitting viewpoint...")}>
              <Card>
                <div className="viewpoint-actions">
                  {props.showViewInBimViewer && props.issueModels?.length !== 0 && (
                    <Tooltip
                      title={
                        hasAnyViewableModel(props.issueModels)
                          ? t("View with BIM Viewer")
                          : t("BIM Viewer (being processed)")
                      }
                      placement="bottom"
                    >
                      <div
                        className="mr-2"
                        onClick={() =>
                          hasAnyViewableModel(props.issueModels) && viewViewpoint(selectedViewpoint, props.issueModels)
                        }
                      >
                        <FontAwesomeIcon fixedWidth icon={["far", "bullseye-pointer"]} />
                      </div>
                    </Tooltip>
                  )}
                  {selectedViewpoint?.toSubmit && (
                    <Tooltip title={t("Submit")} placement="bottom">
                      <div className="mr-2" onClick={submitViewpoint} style={{ cursor: "pointer" }}>
                        <FontAwesomeIcon
                          fixedWidth
                          icon={["far", "paper-plane"]}
                          style={{
                            color: "#ff4d4f",
                          }}
                        />
                      </div>
                    </Tooltip>
                  )}
                  <Popconfirm
                    title={t("Are you sure?")}
                    okText={t("Yes")}
                    cancelText={t("No")}
                    onConfirm={removeViewpoint}
                  >
                    <Tooltip title={t("Remove")} placement="bottom">
                      <div className="mr-2">
                        <FontAwesomeIcon fixedWidth icon={["far", "trash-alt"]} />
                      </div>
                    </Tooltip>
                  </Popconfirm>
                </div>

                <Image
                  src={
                    selectedViewpoint.imageSrc ||
                    `data:image/${selectedViewpoint.snapshot?.snapshot_type};base64,${selectedViewpoint.snapshot?.snapshot_data}`
                  }
                  fallback={placeholderPath}
                  preview={false}
                />
              </Card>
            </Spin>
          </Col>
        )}
        <Row justify={"end"}>
          <Button
            style={{ marginRight: 10 }}
            onClick={() => setIsModalOpen(true)}
            type="default"
            loading={submitting}
            faIcon={"image-polaroid"}
            disabled={props.isInImportMode || props.topicDeleted || !hasViewpoints}
          >
            {t("Add Viewpoint")}
          </Button>
          <Button
            htmlType="submit"
            loading={submitting}
            onClick={handleSubmit}
            type="primary"
            disabled={props.isInImportMode || props.topicDeleted}
          >
            {t("Add Comment")}
          </Button>
        </Row>
      </>
    );
  })
);
